/* eslint-disable */
import LazyLoad from "vanilla-lazyload";
import inView from "in-view";
import gsap from "gsap";
import Flickity from "flickity";
import Isotope from "isotope-layout";
import VanillaTilt from "vanilla-tilt";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import display from "@studiometa/tailwind-config/plugins/display";

gsap.registerPlugin(ScrollTrigger, SplitText);
const HTML = document.querySelector("html");

class App {
  constructor() {
    this.lazyload = new LazyLoad({
      unobserve_entered: true,
      elements_selector: ".js-lazy",
      threshold: window.innerHeight * 0.8,
      // eslint-disable-next-line camelcase
      callback_enter: ($el) => {
        if ($el.classList.contains("js-lazy-error-placeholder")) {
          $el.lazyLoadPlaceholder = $el.src;
        } else if ($el.dataset.src == undefined) {
          const figure = $el.closest("figure");
          if (figure) figure.classList.add("img-is-loaded");
        }
      },
      callback_loaded: (el) => {
        const figure = el.closest("figure");
        if (figure) figure.classList.add("img-is-loaded");
      },
      // eslint-disable-next-line camelcase
      callback_error: ($el) => {
        if ($el.lazyLoadPlaceholder) {
          $el.src = $el.lazyLoadPlaceholder;
        }
      },
    });

    /** NEWSLETTER POPIN */
    const triggersNSL = document.querySelectorAll(".trigger-nsl");
    if (triggersNSL) {
      triggersNSL.forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          HTML.classList.toggle("open-nsl-box");
        });
      });

      document.body.addEventListener("click", (e) => {
        if (
          HTML.classList.contains("open-nsl-box") &&
          !e.target.closest(".box-nsl .content") &&
          !e.target.classList.contains(".content")
        ) {
          HTML.classList.remove("open-nsl-box");
        }
      });
    }

    /** TOGGLE MENU */
    const toggles = document.querySelectorAll(".toggle-menu");
    if (toggles) {
      const mainHeader = document.querySelector(".main-header");
      toggles.forEach((toggle) => {
        toggle.addEventListener("click", (e) => {
          e.preventDefault();

          HTML.classList.toggle("open-menu");
          const minH = window.innerWidth < 768 ? 56 : 72;

          gsap.to(mainHeader, {
            height: !HTML.classList.contains("open-menu")
              ? `${minH}px`
              : mainHeader.scrollHeight,
            duration: 0.3,
            ease: "circ.inOut",
          });
        });
      });
    }

    const tables = document.querySelectorAll("table");
    if (tables) {
      tables.forEach((table) => {
        const headers = Array.from(table.querySelectorAll("thead td")).map(
          (th) => th.textContent.trim()
        );

        table.querySelectorAll("tbody tr").forEach((tr) => {
          tr.querySelectorAll("td").forEach((td, index) => {
            td.dataset.label = headers[index];
          });
        });
      });
    }

    this.accordion();
    this.randomizeFocus();
    this.isotope();
    this.cardsFan();
    this.tabs();
    this.marquee();
    this.baseline();
    this.tilt();
    this.carousel();
    this.freeCarousel();
    this.parallax();
    this.parallaxHover();
    this.carouselFilter();
    this.inViewport();
  }

  randomizeFocus() {
    const elts = document.querySelectorAll(".randomize-focus");

    if (window.innerWidth < 992) return;

    if (elts) {
      elts.forEach((el) => {
        const cards = el.querySelectorAll(".card");
        const coords = [
          {
            xPercent: -1,
            yPercent: -5,
            rotation: -2,
          },
          {
            xPercent: 1,
            yPercent: 2,
            rotation: 0,
          },
          {
            xPercent: 2,
            yPercent: -6,
            rotation: 4,
          },
          {
            xPercent: -2,
            yPercent: 3,
            rotation: -2,
          },
        ];

        const containerW = el.clientWidth;
        const cardsLength = cards.length;
        let currentPortion = 0;
        const cardContent = el.querySelectorAll(".card .card-content");

        cards.forEach((card, i) => {
          gsap.set(card, coords[i % 4]);
        });

        function resetPortion(index) {
          const opts = {
            xPercent: (Math.random() - 0.5) * 15,
            yPercent: (Math.random() - 0.5) * 10,
            rotation: (Math.random() - 0.5) * 10,
            scale: 1,
            duration: 0.8,
            ease: "elastic.out(1, 0.75)",
          };
          // Last active card
          gsap.to(cards[index], opts);
        }

        function newPortion(i) {
          gsap.to(cards[i], {
            // Reset transformation attributes
            xPercent: 0,
            yPercent: 0,
            rotation: 0,
            duration: 0.8,
            scale: 1.05,
            ease: "elastic.out(1, 0.75)", // Elastic movement at the end (out)
          });

          // For each card's child element
          cardContent.forEach((cardContent, index) => {
            // If it's not the active card
            if (index !== i) {
              gsap.to(cardContent, {
                // When index - i < 0, push left
                // When index - i > 0, push right
                // The further (index - i) moves from 0 in both ways,
                // the smaller the displacement
                xPercent: 12 / (index - i),
                ease: "elastic.out(1, 0.75)",
                duration: 0.8,
              });
              // If it is the active card
            } else {
              // Center its child
              gsap.to(cardContent, {
                xPercent: 0,
                ease: "elastic.out(1, 0.75)",
                duration: 0.8,
              });
            }
          });
        }

        el.addEventListener("mousemove", (e) => {
          // Cursor position relative to the left edge of the container
          const mouseX = e.clientX - el.getBoundingClientRect().left;

          // Cursor’s horizontal percentage within the container
          const percentage = mouseX / containerW;

          // Round the value up to get a valid index
          const activePortion = Math.ceil(percentage * cardsLength);

          // If a new portion is hovered
          if (
            currentPortion !== activePortion &&
            activePortion > 0 &&
            activePortion <= cardsLength
          ) {
            // If a portion was already hovered, reset it
            // -1 to target the correct index in the card set
            if (currentPortion !== 0) {
              resetPortion(currentPortion - 1);
            }

            // Update the index of the new portion
            currentPortion = activePortion;
            // -1 to target the correct index in the card set
            newPortion(currentPortion - 1);
          }
        });

        el.addEventListener("mouseleave", () => {
          // -1 to target the correct index in the card set
          resetPortion(currentPortion - 1);

          // No portion is hovered anymore
          currentPortion = 0;
          // Recenter all direct child elements of the cards
          gsap.to(cardContent, {
            xPercent: 0,
            ease: "elastic.out(1, 0.75)",
            duration: 0.8,
          });
        });
      });
    }
  }

  cardsFan() {
    const elts = document.querySelectorAll(".cards-container");

    if (elts && window.innerWidth > 600) {
      elts.forEach((root) => {
        const angle = window.innerWidth <= 2000 ? 3 : 1.5;
        const cardWrappers = root.querySelectorAll(".card-wrapper");
        const cards = root.querySelectorAll(".card");
        const totalCards = cards.length - 1;

        cardWrappers.forEach((el, index) => {
          // Calculates the initial rotation for each 'cardWrapper'
          // - 'angle * index': Spreads cards evenly based on their index
          // - '(angle * totalCards / 2)': Centers the distribution by offsetting half of the total spread
          const firstRot = angle * index - (angle * totalCards) / 2;
          // Instantly applies the calculated rotation to each card
          gsap.set(el, { rotation: firstRot });
        });

        // Reveal Animation
        gsap.from(cardWrappers, {
          rotation: 20, // Starting rotation
          stagger: 0.07, // Delay between each start
          ease: "elastic.out(1, 0.75)", // Elastic movement
          duration: 1.5,
          scrollTrigger: {
            trigger: root, // Listens to the position of root
            start: "top 80%", // Starts when the top of root is at 20% of the height of the viewport
          },
          onComplete: () => {
            root.classList.add("on"); // When the animation is complete
          },
        });

        let timer = 0;
        cardWrappers.forEach((wrapper, i) => {
          const card = wrapper.querySelector(".card");
          wrapper.addEventListener("mouseenter", () => {
            clearTimeout(timer);
            gsap.to(card, {
              x: "0%",
              zIndex: 2,
              y: "-6%",
              ease: "power2.inOut",
              duration: 0.3,
            });
            cards.forEach((c, j) => {
              if (j != i) {
                const dir = j > i ? 40 : -20;
                gsap.to(c, {
                  zIndex: 1,
                  y: "0%",
                  x: `${dir}%`,
                  ease: "power2.inOut",
                  duration: 0.3,
                });
              }
            });
          });
          wrapper.addEventListener("mouseleave", () => {
            timer = setTimeout(() => {
              cards.forEach((c) => {
                gsap.to(c, {
                  x: "0%",
                  y: "0%",
                  scale: 1,
                  zIndex: 1,
                });
              });
            }, 50);
          });
        });
      });
    }
  }

  carouselFilter() {
    const elts = document.querySelectorAll(".carousel-filter");

    if (elts) {
      elts.forEach((container) => {
        const carousel = container.querySelector(".carousel-content");
        if (carousel) {
          const flkty = new Flickity(carousel, {
            cellAlign: "left",
            contain: true,
            prevNextButtons: false,
            pageDots: false,
            groupCells: true,
          });

          const items = carousel.querySelectorAll(".item");

          gsap.set(items, {
            clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
          });
          function filterCarousel(filter) {
            const itemsVisible = [];

            flkty.select(0);

            items.forEach((item) => {
              const match = filter === "*" || item.classList.contains(filter);

              if (match) {
                itemsVisible.push(item);

                gsap.to(item, {
                  // width: "16.666%",
                  clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
                  opacity: 1,
                  duration: 0.45,
                  display: "block",
                  ease: "power2.out",
                });
              } else {
                gsap.to(item, {
                  // width: 0,
                  clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)",
                  opacity: 0,
                  duration: 0.35,
                  ease: "power2.in",
                  onComplete: () => {
                    gsap.set(item, { display: "none" });
                  },
                });
              }
            });

            itemsVisible.forEach((item, i) => {
              gsap.to(item, {
                x: `${i * 100}%`,
                duration: 0.3,
                ease: "circ.inOut",
                delay: 0.2,
              });
            });

            setTimeout(() => {
              flkty.resize();
              flkty.reposition();
            }, 450);
          }

          // Filter
          const filters = container.querySelectorAll(".filter a");
          if (filters) {
            filters.forEach((filter) => {
              filter.addEventListener("click", function (e) {
                e.preventDefault();
                filters.forEach((f) => f.classList.remove("active"));
                filter.classList.add("active");
                filterCarousel(this.dataset.group);
              });
            });
          }

          const select = container.querySelector("select.filter");
          if (select) {
            select.addEventListener("change", () => {
              filterCarousel(select.value);
            });
          }

          const nav = container.querySelectorAll(".carousel-nav a");
          if (nav) {
            nav.forEach((btn) => {
              btn.addEventListener("click", (e) => {
                e.preventDefault();
                if (btn.classList.contains("prev")) {
                  flkty.previous();
                } else {
                  flkty.next();
                }
              });
            });
          }
        }
      });
    }
  }

  marquee() {
    const elts = document.querySelectorAll(".marquee .line");
    if (elts) {
      elts.forEach((track) => {
        track.innerHTML += track.innerHTML;

        this.lazyload.update();

        let direction = 1; // 1 = vers la gauche, -1 = vers la droite
        const speed = 40; // pixels par seconde
        let x = 0; // position actuelle
        let wrapWidth; // largeur d’un bloc A
        let lastScrollY = window.scrollY;

        function computeWrapWidth() {
          // On divise par 2 car on a mis A + A
          wrapWidth = track.scrollWidth / 2;
        }

        // 2) Calcul initial + recalcul au resize
        window.addEventListener("load", computeWrapWidth);
        window.addEventListener("resize", computeWrapWidth);

        // 3) Détection du sens de scroll
        window.addEventListener(
          "scroll",
          () => {
            const currentScrollY = window.scrollY;

            if (currentScrollY > lastScrollY) {
              // Scroll vers le bas → texte va vers la gauche
              direction = 1;
            } else if (currentScrollY < lastScrollY) {
              // Scroll vers le haut → texte va vers la droite
              direction = -1;
            }

            lastScrollY = currentScrollY;
          },
          { passive: true }
        );

        // 4) Animation avec GSAP.ticker (boucle infinie)
        let lastTime = performance.now();

        gsap.ticker.add(() => {
          const now = performance.now();
          const dt = (now - lastTime) / 1000; // secondes écoulées
          lastTime = now;

          if (!wrapWidth) return; // au cas où le layout n’est pas encore prêt

          // Mise à jour de la position en fonction du temps, de la vitesse et du sens
          x += direction * speed * dt;

          // 5) Normalisation dans l’intervalle [-wrapWidth, 0]
          //    pour boucler proprement dans les deux sens.
          if (x <= -wrapWidth) {
            x += wrapWidth;
          } else if (x >= 0) {
            x -= wrapWidth;
          }

          gsap.set(track, { x }); // applique transform: translateX(x)
        });
      });
    }
  }

  tilt() {
    VanillaTilt.init(document.querySelectorAll(".js-tilt"), {
      max: 14,
      speed: 200,
    });
  }

  tabs() {
    const elts = document.querySelectorAll(".tabs");

    if (elts) {
      elts.forEach((el) => {
        let current = -1;
        const headers = el.querySelectorAll(".tabs-header a");
        const items = el.querySelectorAll(".tabs-item");
        const container = el.querySelector(".tabs-container");

        if (headers && items) {
          gsap.set(container, {
            height: items[0].scrollHeight,
            aspectRatio: "none",
          });

          function goto(index) {
            if (current == index) return;
            let delay = 0;
            const duration = 0.3;
            const dir = index > current ? 1 : -1;

            // HIDE OLD
            if (current >= 0) {
              gsap.to(items[current], {
                autoAlpha: 0,
                x: `${-4 * dir}rem`,
                duration,
                ease: "power2.inOut",
              });

              headers[current].classList.remove("active");

              delay = 0.1;
            }

            // SHOW THE NEW
            gsap.fromTo(
              items[index],
              {
                autoAlpha: 0,
                x: `${4 * dir}rem`,
              },
              {
                autoAlpha: 1,
                x: "0rem",
                duration,
                delay,
                ease: "power2.inOut",
              }
            );

            gsap.to(container, {
              height: items[index].scrollHeight,
              aspectRatio: "none",
              duration,
              delay,
              ease: "power2.inOut",
            });

            headers[index].classList.add("active");

            current = index;
          }

          el.addEventListener("inview", () => {
            goto(0);
          });

          headers.forEach((trigger, i) => {
            trigger.addEventListener("click", (e) => {
              e.preventDefault();

              goto(i);
            });
          });
        }
      });
    }
  }

  parallax() {
    const elts = document.querySelectorAll("[data-speed]");

    if (elts) {
      elts.forEach((el) => {
        const speed = Number.parseFloat(el.dataset.speed) || 1;
        const lag = Number.parseFloat(el.dataset.lag) || 0;

        const y = 0;
        let targetY = 0;

        ScrollTrigger.create({
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          onUpdate: (self) => {
            targetY = (1 - speed) * self.progress * 200;

            if (lag) {
              gsap.to(el, {
                y: targetY,
                duration: lag,
                ease: "power3.out",
                overwrite: true,
              });
            } else {
              gsap.set(el, { y: targetY });
            }
          },
        });
      });
    }
  }

  parallaxHover() {
    const elts = document.querySelectorAll("[data-parallax]");

    if (elts) {
      elts.forEach((el) => {
        const inners = el.querySelectorAll("[data-parallax-move]");

        el.addEventListener("mousemove", (e) => {
          const rect = el.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          // Normalisation entre -1 et 1
          const xNorm = (x / rect.width) * 2 - 1;
          const yNorm = (y / rect.height) * 2 - 1;

          inners.forEach((inner) => {
            // Valeurs max de déplacement (en %)
            const maxMove =
              ((inner.dataset.parallaxMove ? inner.dataset.parallaxMove : 1) /
                100) *
              Number.parseFloat(el.offsetWidth);

            gsap.to(inner, {
              x: xNorm * maxMove,
              y: yNorm * maxMove,
              duration: 0.4,
              ease: "power2.out",
            });
          });
        });

        el.addEventListener("mouseleave", () => {
          inners.forEach((inner) => {
            gsap.to(inner, {
              x: 0,
              y: 0,
              duration: 0.6,
              ease: "power3.out",
            });
          });
        });
      });
    }
  }

  carousel() {
    const elts = document.querySelectorAll(".carousel");

    if (elts) {
      elts.forEach((el) => {
        new Flickity(el, {
          pageDots: false,
          contain: true,
          groupCells: true,
          cellAlign: "left",
        });
      });
    }
  }

  freeCarousel() {
    const elts = document.querySelectorAll(".freescroll-carousel");

    if (elts) {
      elts.forEach((el) => {
        const flkty = new Flickity(el, {
          pageDots: false,
          prevNextButtons: false,
          freeScroll: true,
          wrapAround: true,
          cellAlign: "center",
        });

        let isPaused = false;
        el.addEventListener("mouseenter", () => {
          isPaused = true;
        });

        el.addEventListener("mouseleave", () => {
          isPaused = false;
        });

        function animate() {
          if (!isPaused) {
            flkty.x -= 0.3;
            flkty.positionSlider();
          }
          requestAnimationFrame(animate);
        }

        animate();
      });
    }
  }

  baseline() {
    const elts = document.querySelectorAll(".baseline");

    if (elts) {
      elts.forEach((el) => {
        new SplitText(el.querySelectorAll(".base"), {
          type: "lines",
        });
        const split = new SplitText(el.querySelectorAll(".hover"), {
          type: "lines",
        });
        el.classList.add("ready");

        const duration = window.innerHeight > window.innerWidth ? 180 : 220;
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            end: `+=${duration}% 80%`,
            scrub: true,
            markers: false,
          },
        });

        split.lines.forEach((target) => {
          tl.to(target, {
            clipPath: "polygon(0 0, 110% 0, 110% 100%, 0 100%)",
            ease: "none",
          });
        });
      });
    }
  }

  isotope() {
    const elts = document.querySelectorAll(".isotope-grid");

    if (elts) {
      elts.forEach((el) => {
        const iso = new Isotope(el, {
          itemSelector: ".item",
          layoutMode: "masonry",
        });

        const filters = document.querySelectorAll(".filter a");
        if (filters) {
          filters.forEach((filter) => {
            filter.addEventListener("click", (e) => {
              e.preventDefault();

              if (filter.classList.contains("active")) return false;

              filters.forEach((f) => f.classList.remove("active"));

              filter.classList.add("active");
              iso.arrange({ filter: filter.dataset.filter });
            });
          });
        }

        const select = document.querySelector("select.filter");
        if (select) {
          select.addEventListener("change", () => {
            iso.arrange({ filter: select.value });
          });
        }
      });
    }
  }

  accordion() {
    const elts = document.querySelectorAll(".accordion");

    if (elts) {
      elts.forEach((el) => {
        const items = el.querySelectorAll(".accordion-item");
        const headers = el.querySelectorAll(".accordion-header");
        const contents = el.querySelectorAll(".accordion-content");

        if (items && headers && contents) {
          headers.forEach((header, i) => {
            header.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();

              items[i].classList.toggle("active");

              gsap.to(contents[i], {
                duration: 0.3,
                ease: "power1.inOut",
                autoAlpha: items[i].classList.contains("active") ? 1 : 0,
                height: items[i].classList.contains("active")
                  ? contents[i].scrollHeight
                  : 0,
              });
            });
          });
        }
      });
    }
  }

  inViewport() {
    // In viewport
    inView.offset({
      bottom: window.innerHeight * 0.1,
    });
    inView(".js-reveal:not(.is-visible)")
      .on("enter", function (elt) {
        elt.dispatchEvent(new Event("inview"));
        elt.classList.add("is-visible");
        elt.classList.remove("not-visible");

        const video = elt.querySelector("video");

        if (
          (elt.tagName == "VIDEO" && elt.dataset.autoplay == "true") ||
          (video && video.dataset.autoplay == "true")
        ) {
          const v = video || elt;
          const playPromise = v.play();
          if (playPromise !== null) {
            playPromise.catch(() => {
              v.play();
            });
          }
        }
      })
      .on("exit", function (elt) {
        const video = elt.querySelector("video");
        if (
          (elt.tagName == "VIDEO" && elt.dataset.autoplay != "") ||
          (video && video.dataset.autoplay != "")
        ) {
          const v = video || elt;
          v.pause();
        }

        elt.dispatchEvent(new Event("outview"));
        elt.classList.add("not-visible");
      });
  }
}

window.addEventListener("DOMContentLoaded", () => {
  new App();
});
